import { useFetch } from '@/composables/useFetch';
import { usePlatform } from '@/composables/usePlatform';
import router from '@/router';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { SubscriptionPlan } from '@/types/api';
import { useToast } from 'primevue/usetoast';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Import Cordova Purchase plugin via window object
interface CdvPurchaseProduct {
  id: string;
  canPurchase: boolean;
  title?: string; // Add missing property
  description?: string; // Add missing property
  pricing?: {
    currency: string;
    priceMicros: number;
    price?: string; // Add missing property for formatted price
  };
  getOffer: () => unknown;
}

interface CdvPurchaseTransaction {
  transactionId: string;
  products: Array<{ id: string }>;
  state?: string; // Transaction state (initiated, approved, finished, etc.)
  isAcknowledged?: boolean; // Whether the transaction has been acknowledged
  isPending?: boolean; // Whether the transaction is pending
}

interface CdvPurchaseReceipt {
  nativeData: unknown;
  transactions: CdvPurchaseTransaction[];
  platform?: string; // Platform identifier (ios-appstore, android-playstore)
  verify: () => void;
  finish: () => void;
}

interface CdvPurchaseStore {
  verbosity: number;
  products: CdvPurchaseProduct[];
  register: (product: unknown) => void;
  initialize: (platforms: unknown[]) => Promise<void>;
  when: () => {
    productUpdated: (callback: (product: CdvPurchaseProduct) => void) => void;
    approved: (callback: (transaction: CdvPurchaseReceipt) => void) => void;
    verified: (callback: (receipt: CdvPurchaseReceipt) => void) => void;
    finished: (callback: (transaction: unknown) => void) => void;
    receiptUpdated: (callback: (receipt: CdvPurchaseReceipt) => void) => void;
    unverified: (callback: (info: unknown) => void) => void;
    pending: (callback: (transaction: unknown) => void) => void;
  };
  error: (callback: (error: { code: number; message?: string }) => void) => void;
  get: (productId: string, platform: unknown) => CdvPurchaseProduct | undefined;
  order: (offer: unknown) => Promise<unknown>;
  restorePurchases: () => Promise<void>;
  /** Present Apple's native offer code redemption sheet (iOS 14+) */
  presentCodeRedemptionSheet: () => Promise<void>;
  /** Open the platform's subscription management UI */
  manageSubscriptions: (platform?: unknown) => Promise<unknown>;
}

interface CdvPurchasePlugin {
  store: CdvPurchaseStore;
  LogLevel: { DEBUG: number };
  ProductType: { PAID_SUBSCRIPTION: string };
  Platform: { APPLE_APPSTORE: unknown; GOOGLE_PLAY: unknown };
  ErrorCode: { PAYMENT_CANCELLED: number };
}

declare const CdvPurchase: CdvPurchasePlugin;

// Global state - shared across all composable instances
const isInitialized = ref(false);
const isInitializing = ref(false);
const isProcessing = ref(false);
const availableProducts = ref<CdvPurchaseProduct[]>([]);
const eventHandlersSetup = ref(false);

// Polling control - also global to prevent multiple intervals
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let pollingValidationDone = false;

export function useInAppPurchase() {
  const { isNative, isIOS } = usePlatform();
  const { fetchData } = useFetch();
  const envStore = useEnvStore();
  const userStore = useUserStore();
  const toast = useToast();
  const { t } = useI18n();

  /**
   * Initialize the IAP store
   */
  const initializeStore = async () => {
    // Prevent double initialization
    if (!isNative.value) {
      console.log('[IAP] Not native platform, skipping initialization');
      return;
    }

    if (isInitialized.value) {
      console.log('[IAP] Already initialized, skipping');
      return;
    }

    if (isInitializing.value) {
      console.log('[IAP] Initialization already in progress, skipping');
      return;
    }

    isInitializing.value = true;

    try {
      console.log('[IAP] Initializing store...');
      console.log('[IAP] Platform:', isIOS.value ? 'iOS' : 'Android');

      // Check if CdvPurchase is available
      if (typeof CdvPurchase === 'undefined') {
        console.error('[IAP] CdvPurchase plugin not available');
        return;
      }

      // Platform configuration
      const platform = isIOS.value ? CdvPurchase.Platform.APPLE_APPSTORE : CdvPurchase.Platform.GOOGLE_PLAY;

      CdvPurchase.store.verbosity = CdvPurchase.LogLevel.DEBUG;

      // Check if store already has products (was initialized in a previous session)
      if (CdvPurchase.store.products.length > 0) {
        console.log('[IAP] Store already has products, marking as initialized');
        isInitialized.value = true;
        setupEventHandlers();
        loadProducts();
        return;
      }

      // Register products based on your plans
      const products = [
        // Lite
        {
          id: 'ai.gastronomia.app.lite_yearly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        // Basic
        {
          id: 'ai.gastronomia.app.pro_basic_monthly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        {
          id: 'ai.gastronomia.app.pro_basic_yearly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        // Advanced
        {
          id: 'ai.gastronomia.app.pro_advanced_monthly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        {
          id: 'ai.gastronomia.app.pro_advanced_yearly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        // Premium
        {
          id: 'ai.gastronomia.app.pro_premium_monthly',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
        {
          id: 'ai.gastronomia.app.pro_premium_annual',
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
          platform: platform,
        },
      ];

      console.log('[IAP] Registering products...');
      products.forEach((product) => {
        console.log('[IAP] Registering:', product.id);
        CdvPurchase.store.register(product);
      });

      console.log('[IAP] Products registered:', products.length);

      // Setup event handlers BEFORE initialization
      setupEventHandlers();

      // Initialize the store
      console.log('[IAP] Calling store.initialize()...');
      await CdvPurchase.store.initialize([platform]);

      isInitialized.value = true;
      console.log('[IAP] Store initialized successfully');

      // Wait longer for products to load (increased from 2s to 5s)
      setTimeout(() => {
        console.log('[IAP] ========== PRODUCT CHECK AFTER 5 SECONDS ==========');
        console.log('[IAP] Total products in store:', CdvPurchase.store.products.length);

        if (CdvPurchase.store.products.length === 0) {
          console.error('[IAP] ❌ NO PRODUCTS LOADED FROM APP STORE');
          console.error('[IAP] Possible reasons:');
          console.error('[IAP] 1. Products not yet synced to sandbox (wait 2-4 hours after creation)');
          console.error("[IAP] 2. Product IDs don't match exactly");
          console.error('[IAP] 3. Sandbox account not properly signed in');
          console.error('[IAP] 4. Products not in "Ready to Submit" state');
        } else {
          console.log('[IAP] ✓ Products loaded successfully');
          CdvPurchase.store.products.forEach((product) => {
            console.log('[IAP] Product:', {
              id: product.id,
              canPurchase: product.canPurchase,
              pricing: product.pricing,
            });
          });
        }

        loadProducts();
      }, 5000); // Increased timeout
    } catch (error) {
      console.error('[IAP] Failed to initialize store:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.iap.initialization_failed'),
        life: 5000,
      });
    } finally {
      isInitializing.value = false; // Always reset
    }
  };

  /**
   * Setup event handlers for purchase flow
   */
  const setupEventHandlers = () => {
    // Prevent setting up handlers multiple times
    if (eventHandlersSetup.value) {
      console.log('[IAP] Event handlers already set up, skipping');
      return;
    }

    console.log('[IAP] Setting up event handlers...');
    eventHandlersSetup.value = true;

    // When a product is updated
    CdvPurchase.store.when().productUpdated((product) => {
      console.log('[IAP] Product updated:', product);
      updateAvailableProducts();
    });

    // When a purchase is approved
    CdvPurchase.store.when().approved((transaction) => {
      console.log('[IAP] ========== PURCHASE APPROVED ==========');
      console.log('[IAP] Transaction object:', JSON.stringify(transaction, null, 2));
      console.log('[IAP] Transaction type:', typeof transaction);
      console.log('[IAP] Has verify method:', typeof transaction.verify === 'function');
      console.log('[IAP] Has transactions:', Array.isArray(transaction.transactions));

      if (typeof transaction.verify === 'function') {
        console.log('[IAP] Calling verify()...');
        transaction.verify();
      } else {
        console.error('[IAP] No verify method on transaction object!');
      }
    });

    // When a purchase is verified
    CdvPurchase.store.when().verified(async (receipt) => {
      console.log('[IAP] ========== PURCHASE VERIFIED ==========');
      console.log('[IAP] Receipt object keys:', Object.keys(receipt));
      console.log('[IAP] Receipt has nativeData:', !!receipt.nativeData);
      console.log('[IAP] Receipt nativeData type:', typeof receipt.nativeData);
      console.log('[IAP] Receipt transactions count:', receipt.transactions?.length || 0);

      // IMPORTANT: Only process if a purchase is actively in progress
      // This prevents validation attempts during store initialization
      if (!isProcessing.value) {
        console.log('[IAP] verified: No purchase in progress, skipping validation');
        return;
      }

      // Stop polling since we got the verified event
      pollingValidationDone = true;
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }

      if (receipt.transactions && receipt.transactions.length > 0) {
        receipt.transactions.forEach((t, i) => {
          console.log(`[IAP] Transaction ${i}:`, {
            transactionId: t.transactionId,
            products: t.products?.map((p) => p.id),
          });
        });
      }

      try {
        console.log('[IAP] Calling validateReceiptWithBackend...');
        // Send receipt to backend for validation
        await validateReceiptWithBackend(receipt);
        console.log('[IAP] Backend validation successful, calling finish()');
        receipt.finish();

        // Navigate to success page after successful validation
        isProcessing.value = false;
        console.log('[IAP] Refreshing user data and navigating to success...');
        await userStore.refreshUser();
        router.push('/subscription/success');
      } catch (error) {
        console.error('[IAP] Failed to validate receipt with backend:', error);
        // Still finish the receipt to prevent it from being stuck
        // The subscription can be restored later if needed
        console.log('[IAP] Finishing receipt despite validation error');
        receipt.finish();
        isProcessing.value = false;

        // Show error to user
        toast.add({
          severity: 'warn',
          summary: t('common.warning'),
          detail: t('subscription.iap.validation_pending'),
          life: 8000,
        });
      }
    });

    // When a purchase is finished
    CdvPurchase.store.when().finished(async (transaction) => {
      console.log('[IAP] ========== PURCHASE FINISHED ==========');
      console.log('[IAP] Purchase finished:', transaction);
      isProcessing.value = false;

      // Refresh user data to get updated subscription
      console.log('[IAP] Refreshing user data...');
      await userStore.refreshUser();
      console.log('[IAP] User data refreshed');

      // Navigate to success page (same as Stripe flow)
      // console.log('[IAP] Navigating to success page...');
      // router.push('/subscription/success');
    });

    // When a receipt is updated (happens before verified)
    // On iOS, this is the most reliable event to catch the completed purchase
    CdvPurchase.store.when().receiptUpdated(async (receipt) => {
      console.log('[IAP] ========== RECEIPT UPDATED ==========');
      console.log('[IAP] Receipt updated:', {
        transactionCount: receipt.transactions?.length || 0,
        hasNativeData: !!receipt.nativeData,
        nativeDataType: typeof receipt.nativeData,
        platform: receipt.platform,
        pollingValidationDone: pollingValidationDone,
        isProcessing: isProcessing.value,
      });

      // Log transaction states
      if (receipt.transactions) {
        receipt.transactions.forEach((t, i) => {
          console.log(`[IAP] Transaction ${i}:`, {
            transactionId: t.transactionId,
            state: t.state,
            products: t.products?.map((p) => p.id),
            isAcknowledged: t.isAcknowledged,
            isPending: t.isPending,
          });
        });
      }

      // Skip if validation already done
      if (pollingValidationDone) {
        console.log('[IAP] receiptUpdated: Validation already done, skipping');
        return;
      }

      // IMPORTANT: Only process if a purchase is actively in progress
      // This prevents validation attempts when just navigating the app
      if (!isProcessing.value) {
        console.log('[IAP] receiptUpdated: No purchase in progress, skipping validation');
        return;
      }

      // Check if we have a completed transaction that needs validation
      // This is a fallback for iOS where 'approved' event might not fire correctly
      const completedTransaction = receipt.transactions?.find(
        (t) =>
          t.products?.some((p) => p.id?.startsWith('ai.gastronomia.app.')) && (t.state === 'approved' || t.state === 'finished') && !t.isAcknowledged,
      );

      if (completedTransaction && receipt.nativeData) {
        console.log('[IAP] Found completed transaction in receiptUpdated, attempting validation...');
        pollingValidationDone = true;

        // Stop polling
        if (pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
        }

        try {
          await validateReceiptWithBackend(receipt);
          console.log('[IAP] Backend validation successful from receiptUpdated');
          receipt.finish();

          // Navigate to success page after successful validation
          isProcessing.value = false;
          console.log('[IAP] Refreshing user data and navigating to success...');
          await userStore.refreshUser();
          router.push('/subscription/success');
        } catch (error) {
          console.error('[IAP] Backend validation failed from receiptUpdated:', error);
          // Still finish to prevent stuck state
          receipt.finish();
          isProcessing.value = false;

          // Show error but still navigate - the webhook might have already created the subscription
          toast.add({
            severity: 'warn',
            summary: t('common.warning'),
            detail: t('subscription.iap.validation_pending'),
            life: 8000,
          });
        }
      }
    });

    // Handle unverified receipts (iOS specific - when receipt exists but wasn't verified through normal flow)
    CdvPurchase.store.when().unverified((unverifiedInfo) => {
      console.log('[IAP] ========== UNVERIFIED RECEIPT ==========');
      console.log('[IAP] Unverified info:', JSON.stringify(unverifiedInfo, null, 2));
    });

    // Handle pending transactions
    CdvPurchase.store.when().pending((transaction) => {
      console.log('[IAP] ========== TRANSACTION PENDING ==========');
      console.log('[IAP] Pending transaction:', JSON.stringify(transaction, null, 2));
    });

    CdvPurchase.store.error((error) => {
      console.error('[IAP] Store error:', error);
      isProcessing.value = false;

      if (error.code !== CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: error.message || t('subscription.iap.purchase_failed'),
          life: 5000,
        });
      }
    });
  };

  /**
   * Load available products
   */
  const loadProducts = () => {
    console.log('[IAP] Loading products...');
    updateAvailableProducts();
  };

  /**
   * Update available products list
   */
  const updateAvailableProducts = () => {
    const products = CdvPurchase.store.products;
    availableProducts.value = products.filter((p) => p.canPurchase);
    console.log('[IAP] Available products:', availableProducts.value.length);
  };

  /**
   * Explicit Apple product ID overrides for plans whose App Store Connect ID
   * does not follow the standard generated pattern.
   * Key: `${planName}:${billingPeriod}`
   */
  const APPLE_PRODUCT_ID_OVERRIDES: Record<string, string> = {
    // Premium yearly was registered in App Store Connect as 'pro_premium_annual'
    // because 'pro_premium_yearly' was not available.
    'Premium:yearly': 'ai.gastronomia.app.pro_premium_annual',
  };

  /**
   * Get product ID for a plan.
   * Checks explicit overrides first, then falls back to the standard pattern:
   * ai.gastronomia.app.{planSlug}_{billingPeriod}
   */
  const getProductIdForPlan = (plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly'): string => {
    const overrideKey = `${plan.name}:${billingPeriod}`;
    if (APPLE_PRODUCT_ID_OVERRIDES[overrideKey]) {
      return APPLE_PRODUCT_ID_OVERRIDES[overrideKey];
    }

    // Standard pattern: ai.gastronomia.app.{planSlug}_{billingPeriod}
    const planNameMap: { [key: string]: string } = {
      Freemium: 'freemium',
      Lite: 'lite',
      Basic: 'pro_basic',
      Advanced: 'pro_advanced',
      Premium: 'pro_premium',
    };

    const planSlug = planNameMap[plan.name] || plan.name.toLowerCase();
    return `ai.gastronomia.app.${planSlug}_${billingPeriod}`;
  };

  /**
   * Purchase a subscription
   */
  const purchaseSubscription = async (plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly') => {
    if (!isInitialized.value) {
      console.error('[IAP] Store not initialized');
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.iap.not_initialized'),
        life: 5000,
      });
      return false;
    }

    if (isProcessing.value) {
      console.log('[IAP] Purchase already in progress');
      return false;
    }

    try {
      isProcessing.value = true;
      const productId = getProductIdForPlan(plan, billingPeriod);

      console.log('[IAP] ========== PURCHASE ATTEMPT ==========');
      console.log('[IAP] Target product:', productId);
      console.log('[IAP] Plan:', plan.name);
      console.log('[IAP] Billing period:', billingPeriod);
      console.log('[IAP] Total products in store:', CdvPurchase.store.products.length);
      console.log(
        '[IAP] Available products:',
        CdvPurchase.store.products.map((p) => ({
          id: p.id,
          canPurchase: p.canPurchase,
          hasPricing: !!p.pricing,
        })),
      );

      const platform = isIOS.value ? CdvPurchase.Platform.APPLE_APPSTORE : CdvPurchase.Platform.GOOGLE_PLAY;
      const product = CdvPurchase.store.get(productId, platform);

      if (!product) {
        const availableIds = CdvPurchase.store.products.map((p) => p.id).join(', ');
        const errorMsg = `Product not found: ${productId}. Available products: ${availableIds || 'NONE'}`;
        console.error('[IAP]', errorMsg);
        throw new Error(errorMsg);
      }

      console.log('[IAP] Product found:', {
        id: product.id,
        canPurchase: product.canPurchase,
        pricing: product.pricing,
      });

      if (!product.canPurchase) {
        throw new Error(`Product cannot be purchased: ${productId}`);
      }

      // Get offers (for introductory pricing, trials, etc.)
      const offers = product.getOffer();
      console.log('[IAP] Offers:', offers);

      if (!offers) {
        throw new Error(`No offers available for product: ${productId}`);
      }

      console.log('[IAP] Starting order...');

      // Start the purchase
      const result = await CdvPurchase.store.order(offers);
      console.log('[IAP] Order initiated:', result);

      // On iOS, the 'approved' event might not fire properly in some scenarios
      // Start a fallback mechanism to check for completed purchases
      if (isIOS.value) {
        console.log('[IAP] Starting iOS fallback polling for completed purchase...');
        startPurchaseCompletionPolling(productId);
      }

      return true;
    } catch (error) {
      console.error('[IAP] Purchase failed:', error);
      isProcessing.value = false;
      const errorMessage = error instanceof Error ? error.message : String(error);

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: errorMessage || t('subscription.iap.purchase_failed'),
        life: 5000,
      });

      return false;
    }
  };

  /**
   * iOS Fallback: Poll for completed purchase
   * This handles cases where the 'approved' event doesn't fire
   */
  const startPurchaseCompletionPolling = (productId: string) => {
    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    pollingValidationDone = false;

    let pollCount = 0;
    const maxPolls = 30; // Poll for up to 60 seconds (30 * 2s)

    pollingInterval = setInterval(async () => {
      pollCount++;
      console.log(`[IAP] Polling for completed purchase (${pollCount}/${maxPolls})...`);

      if (pollingValidationDone) {
        console.log('[IAP] Validation already done, stopping polling');
        if (pollingInterval) clearInterval(pollingInterval);
        return;
      }

      // Check if we're no longer processing (purchase cancelled or already finished)
      if (!isProcessing.value) {
        console.log('[IAP] Purchase no longer processing, stopping polling');
        if (pollingInterval) clearInterval(pollingInterval);
        return;
      }

      // Stop after max polls
      if (pollCount >= maxPolls) {
        console.log('[IAP] Max polls reached, stopping');
        if (pollingInterval) clearInterval(pollingInterval);
        isProcessing.value = false;
        return;
      }

      try {
        // Check store for completed receipts
        const store = CdvPurchase.store as unknown as Record<string, unknown>;

        // Check localReceipts
        if (Array.isArray(store.localReceipts)) {
          const localReceipts = store.localReceipts as CdvPurchaseReceipt[];
          console.log(`[IAP] Polling: Found ${localReceipts.length} local receipts`);

          for (const receipt of localReceipts) {
            if (pollingValidationDone) break;

            // Check if this receipt has our product and nativeData
            const hasTargetProduct = receipt.transactions?.some((t) =>
              t.products?.some((p) => p.id === productId || p.id?.startsWith('ai.gastronomia.app.')),
            );

            const hasNativeData = !!receipt.nativeData;

            console.log(`[IAP] Polling: Receipt check - hasTargetProduct: ${hasTargetProduct}, hasNativeData: ${hasNativeData}`);

            if (hasTargetProduct && hasNativeData) {
              console.log('[IAP] Polling: Found completed receipt with nativeData!');
              pollingValidationDone = true;

              try {
                await validateReceiptWithBackend(receipt);
                console.log('[IAP] Polling: Backend validation successful');
                receipt.finish();

                // Navigate to success page
                isProcessing.value = false;
                if (pollingInterval) clearInterval(pollingInterval);
                console.log('[IAP] Polling: Refreshing user data and navigating to success...');
                await userStore.refreshUser();
                router.push('/subscription/success');
                return;
              } catch (error) {
                console.error('[IAP] Polling: Backend validation failed:', error);
                receipt.finish();
              }

              if (pollingInterval) clearInterval(pollingInterval);
              return;
            }
          }
        }

        // Also check store.verifiedReceipts
        if (!pollingValidationDone && Array.isArray(store.verifiedReceipts)) {
          const verifiedReceipts = store.verifiedReceipts as CdvPurchaseReceipt[];
          console.log(`[IAP] Polling: Found ${verifiedReceipts.length} verified receipts`);

          for (const receipt of verifiedReceipts) {
            if (pollingValidationDone) break;

            const hasTargetProduct = receipt.transactions?.some((t) =>
              t.products?.some((p) => p.id === productId || p.id?.startsWith('ai.gastronomia.app.')),
            );

            if (hasTargetProduct && receipt.nativeData) {
              console.log('[IAP] Polling: Found verified receipt with nativeData!');
              pollingValidationDone = true;

              try {
                await validateReceiptWithBackend(receipt);
                console.log('[IAP] Polling: Backend validation successful');
                receipt.finish();

                // Navigate to success page
                isProcessing.value = false;
                if (pollingInterval) clearInterval(pollingInterval);
                console.log('[IAP] Polling: Refreshing user data and navigating to success...');
                await userStore.refreshUser();
                router.push('/subscription/success');
                return;
              } catch (error) {
                console.error('[IAP] Polling: Backend validation failed:', error);
                receipt.finish();
              }

              if (pollingInterval) clearInterval(pollingInterval);
              return;
            }
          }
        }
      } catch (error) {
        console.error('[IAP] Polling error:', error);
      }
    }, 2000); // Poll every 2 seconds
  };

  /**
   * Validate receipt with backend
   */
  const validateReceiptWithBackend = async (receipt: CdvPurchaseReceipt) => {
    try {
      console.log('[IAP] ========== VALIDATING RECEIPT WITH BACKEND ==========');
      console.log('[IAP] Receipt object keys:', Object.keys(receipt));
      console.log('[IAP] Receipt transactions count:', receipt.transactions?.length || 0);
      console.log('[IAP] Receipt nativeData type:', typeof receipt.nativeData);

      // Log all transactions for debugging
      if (receipt.transactions) {
        receipt.transactions.forEach((t, i) => {
          console.log(`[IAP] Transaction ${i}:`, {
            transactionId: t.transactionId,
            products: t.products?.map((p) => p.id),
            // Log all keys on the transaction
            keys: Object.keys(t),
          });
        });
      }

      // Find the subscription transaction - try multiple approaches
      let subscriptionTransaction = receipt.transactions?.find(
        (t) => t.products && t.products.length > 0 && t.products[0].id?.startsWith('ai.gastronomia.app.'),
      );

      // Fallback: find any transaction with products that isn't just the app bundle
      if (!subscriptionTransaction) {
        subscriptionTransaction = receipt.transactions?.find((t) => t.products && t.products.length > 0 && t.products[0].id !== 'ai.gastronomia.app');
      }

      // Last resort: use the first transaction with products
      if (!subscriptionTransaction && receipt.transactions?.length > 0) {
        subscriptionTransaction = receipt.transactions.find((t) => t.products && t.products.length > 0);
      }

      if (!subscriptionTransaction) {
        console.error('[IAP] No subscription transaction found in receipt');
        console.error('[IAP] Available transactions:', JSON.stringify(receipt.transactions, null, 2));
        throw new Error('No subscription transaction found in receipt');
      }

      console.log('[IAP] Found subscription transaction:', {
        transactionId: subscriptionTransaction.transactionId,
        productId: subscriptionTransaction.products[0]?.id,
      });

      // Extract the base64 receipt data - handle various formats for iOS
      let receiptData: string | null = null;

      // Method 1: Direct string
      if (typeof receipt.nativeData === 'string' && receipt.nativeData.length > 0) {
        console.log('[IAP] Using nativeData as string');
        receiptData = receipt.nativeData;
      }
      // Method 2: Object with appStoreReceipt property (iOS)
      else if (typeof receipt.nativeData === 'object' && receipt.nativeData !== null) {
        const nativeDataObj = receipt.nativeData as Record<string, unknown>;
        console.log('[IAP] nativeData is object with keys:', Object.keys(nativeDataObj));

        if (typeof nativeDataObj.appStoreReceipt === 'string') {
          console.log('[IAP] Using nativeData.appStoreReceipt');
          receiptData = nativeDataObj.appStoreReceipt;
        } else if (typeof nativeDataObj.receipt === 'string') {
          console.log('[IAP] Using nativeData.receipt');
          receiptData = nativeDataObj.receipt;
        } else if (typeof nativeDataObj.receiptData === 'string') {
          console.log('[IAP] Using nativeData.receiptData');
          receiptData = nativeDataObj.receiptData;
        } else if (typeof nativeDataObj['receipt-data'] === 'string') {
          console.log('[IAP] Using nativeData["receipt-data"]');
          receiptData = nativeDataObj['receipt-data'];
        } else if (typeof nativeDataObj.latestReceipt === 'string') {
          console.log('[IAP] Using nativeData.latestReceipt');
          receiptData = nativeDataObj.latestReceipt;
        }
      }

      // Method 3: Try to access from CdvPurchase store directly (iOS specific)
      if (!receiptData && typeof CdvPurchase !== 'undefined') {
        try {
          const store = CdvPurchase.store as unknown as Record<string, unknown>;
          console.log('[IAP] Checking store for receipt, store keys:', Object.keys(store));

          // Try applicationReceipt
          if (store.applicationReceipt && typeof store.applicationReceipt === 'string') {
            console.log('[IAP] Using CdvPurchase.store.applicationReceipt');
            receiptData = store.applicationReceipt;
          }

          // Try localReceipts (CdvPurchase v13+)
          if (!receiptData && Array.isArray(store.localReceipts)) {
            const localReceipts = store.localReceipts as Array<{ nativeData?: string | object }>;
            console.log('[IAP] Found localReceipts, count:', localReceipts.length);

            for (const localReceipt of localReceipts) {
              if (typeof localReceipt.nativeData === 'string') {
                console.log('[IAP] Using localReceipt.nativeData as string');
                receiptData = localReceipt.nativeData;
                break;
              } else if (typeof localReceipt.nativeData === 'object' && localReceipt.nativeData !== null) {
                const nativeObj = localReceipt.nativeData as Record<string, unknown>;
                if (typeof nativeObj.appStoreReceipt === 'string') {
                  console.log('[IAP] Using localReceipt.nativeData.appStoreReceipt');
                  receiptData = nativeObj.appStoreReceipt;
                  break;
                }
              }
            }
          }

          // Try getLocalReceipts method
          if (!receiptData && typeof store.getLocalReceipts === 'function') {
            const receipts = (store.getLocalReceipts as () => Array<{ nativeData?: string }>)();
            console.log('[IAP] getLocalReceipts returned:', receipts?.length, 'receipts');

            if (receipts && receipts.length > 0 && typeof receipts[0].nativeData === 'string') {
              console.log('[IAP] Using getLocalReceipts()[0].nativeData');
              receiptData = receipts[0].nativeData;
            }
          }
        } catch (e) {
          console.log('[IAP] Could not access store receipts:', e);
        }
      }

      // Method 4: Check if receipt has a platform-specific property
      const receiptAny = receipt as unknown as Record<string, unknown>;
      if (!receiptData && receiptAny.platform === 'ios-appstore') {
        // For iOS, the receipt might be in a different location
        if (typeof receiptAny.appStoreReceipt === 'string') {
          console.log('[IAP] Using receipt.appStoreReceipt directly');
          receiptData = receiptAny.appStoreReceipt;
        }
      }

      // Method 5: Try to get receipt via cordova-plugin-purchase native call
      // This is useful when nativeData isn't populated but the receipt exists
      if (!receiptData && typeof CdvPurchase !== 'undefined') {
        try {
          const adapters = (CdvPurchase as unknown as Record<string, unknown>).Adapters;
          if (adapters && typeof adapters === 'object') {
            const appStoreAdapter = (adapters as Record<string, unknown>).AppStore;
            if (appStoreAdapter && typeof appStoreAdapter === 'object') {
              const appStoreAny = appStoreAdapter as Record<string, unknown>;
              if (typeof appStoreAny.applicationReceipt === 'string') {
                console.log('[IAP] Using CdvPurchase.Adapters.AppStore.applicationReceipt');
                receiptData = appStoreAny.applicationReceipt;
              } else if (typeof appStoreAny.getApplicationReceipt === 'function') {
                const appReceipt = await (appStoreAny.getApplicationReceipt as () => Promise<string>)();
                if (appReceipt) {
                  console.log('[IAP] Using AppStore.getApplicationReceipt()');
                  receiptData = appReceipt;
                }
              }
            }
          }
        } catch (e) {
          console.log('[IAP] Could not access AppStore adapter:', e);
        }
      }

      // Method 6: Check window for any plugin exposing receipt
      if (!receiptData) {
        try {
          const windowAny = window as unknown as Record<string, unknown>;
          // Some plugins expose via window.storekit
          if (windowAny.storekit && typeof windowAny.storekit === 'object') {
            const storekit = windowAny.storekit as Record<string, unknown>;
            if (typeof storekit.appStoreReceipt === 'string') {
              console.log('[IAP] Using window.storekit.appStoreReceipt');
              receiptData = storekit.appStoreReceipt;
            } else if (typeof storekit.getReceipt === 'function') {
              const skReceipt = await (storekit.getReceipt as () => Promise<string>)();
              if (skReceipt) {
                console.log('[IAP] Using window.storekit.getReceipt()');
                receiptData = skReceipt;
              }
            }
          }
        } catch (e) {
          console.log('[IAP] Could not access window.storekit:', e);
        }
      }

      if (!receiptData) {
        console.error('[IAP] Could not extract receipt data');
        console.error('[IAP] nativeData:', JSON.stringify(receipt.nativeData));
        console.error('[IAP] Full receipt:', JSON.stringify(receipt, null, 2));
        throw new Error('Invalid receipt data format - could not extract receipt. nativeData type: ' + typeof receipt.nativeData);
      }

      const payload = {
        receipt_data: receiptData,
        product_id: subscriptionTransaction.products[0].id,
        transaction_id: subscriptionTransaction.transactionId,
      };

      console.log('[IAP] Sending validation payload:', {
        product_id: payload.product_id,
        transaction_id: payload.transaction_id,
        receipt_length: receiptData.length,
      });

      const { data, error } = await fetchData(`${envStore.apiUrl}/subscriptions/apple/validate`, 'POST', payload);

      console.log('[IAP] Backend response:', { data, error, hasError: !!error });

      if (error) {
        console.error('[IAP] Backend validation error:', error);
        throw new Error(typeof error === 'string' ? error : JSON.stringify(error));
      }

      if (!data || !data.success) {
        console.error('[IAP] Backend validation failed:', data);
        throw new Error(data?.message || 'Receipt validation failed');
      }

      console.log('[IAP] Receipt validated successfully:', data);
      return data;
    } catch (error) {
      console.error('[IAP] Backend validation failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[IAP] Error details:', errorMessage);
      throw error;
    }
  };

  /**
   * Restore purchases
   * Required by Apple for App Store approval.
   * Since our backend is the source of truth, this mainly:
   * 1. Satisfies Apple's requirement
   * 2. Provides a fallback if initial validation failed
   */
  const restorePurchases = async () => {
    if (!isInitialized.value) {
      console.error('[IAP] Store not initialized');
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.iap.not_initialized'),
        life: 5000,
      });
      return false;
    }

    try {
      console.log('[IAP] Restoring purchases...');
      isProcessing.value = true;
      pollingValidationDone = false;

      // This will trigger receiptUpdated/verified events if there are valid receipts
      await CdvPurchase.store.restorePurchases();

      // Wait a moment for any receipt events to process
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Refresh user data from backend (the actual source of truth)
      await userStore.refreshUser();

      const hasSubscription = !!userStore.user?.active_restaurant?.subscription;

      isProcessing.value = false;

      if (hasSubscription) {
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('subscription.iap.restore_success'),
          life: 5000,
        });
      } else {
        toast.add({
          severity: 'info',
          summary: t('common.info'),
          detail: t('subscription.iap.no_purchases_found'),
          life: 5000,
        });
      }

      return hasSubscription;
    } catch (error) {
      console.error('[IAP] Restore failed:', error);
      isProcessing.value = false;

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.iap.restore_failed'),
        life: 5000,
      });

      return false;
    }
  };

  /**
   * Get price display for a product
   */
  const getProductPrice = (plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly'): string | null => {
    if (!isInitialized.value) return null;

    const productId = getProductIdForPlan(plan, billingPeriod);
    const product = CdvPurchase.store.get(productId, CdvPurchase.Platform.APPLE_APPSTORE);

    if (!product || !product.pricing) return null;

    return product.pricing.priceMicros ? `${product.pricing.currency} ${(product.pricing.priceMicros / 1000000).toFixed(2)}` : null;
  };

  /**
   * Get Apple IAP formatted price for display
   * Returns the price string from the App Store (e.g., "CHF 9.99" or "$9.99")
   * This is the actual price that will be charged by Apple
   */
  const getApplePrice = (
    plan: SubscriptionPlan,
    billingPeriod: 'monthly' | 'yearly',
  ): { price: string | null; currency: string | null; priceValue: number | null } => {
    if (!isInitialized.value || !isNative.value || !isIOS.value) {
      return { price: null, currency: null, priceValue: null };
    }

    // Freemium is always free
    if (plan.name.toLowerCase() === 'freemium') {
      return { price: '0.00', currency: 'CHF', priceValue: 0 };
    }

    const productId = getProductIdForPlan(plan, billingPeriod);
    const product = CdvPurchase.store.get(productId, CdvPurchase.Platform.APPLE_APPSTORE);

    if (!product || !product.pricing) {
      return { price: null, currency: null, priceValue: null };
    }

    // Use the pre-formatted price from Apple if available
    if (product.pricing.price) {
      const priceValue = product.pricing.priceMicros ? product.pricing.priceMicros / 1000000 : null;
      return {
        price: product.pricing.price,
        currency: product.pricing.currency,
        priceValue,
      };
    }

    // Fallback to calculating from priceMicros
    if (product.pricing.priceMicros) {
      const priceValue = product.pricing.priceMicros / 1000000;
      return {
        price: `${product.pricing.currency} ${priceValue.toFixed(2)}`,
        currency: product.pricing.currency,
        priceValue,
      };
    }

    return { price: null, currency: null, priceValue: null };
  };

  /**
   * Get the monthly equivalent price for Apple IAP
   * For yearly subscriptions, divides by 12 to show monthly equivalent
   */
  const getAppleMonthlyEquivalent = (
    plan: SubscriptionPlan,
    billingPeriod: 'monthly' | 'yearly',
  ): { price: string | null; currency: string | null } => {
    const applePrice = getApplePrice(plan, billingPeriod);

    if (!applePrice.priceValue || !applePrice.currency) {
      return { price: null, currency: null };
    }

    if (billingPeriod === 'yearly') {
      const monthlyEquivalent = applePrice.priceValue / 12;
      return {
        price: monthlyEquivalent.toFixed(2),
        currency: applePrice.currency,
      };
    }

    return {
      price: applePrice.priceValue.toFixed(2),
      currency: applePrice.currency,
    };
  };

  /**
   * Check if a product is available
   */
  const isProductAvailable = (plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly'): boolean => {
    if (!isInitialized.value) return false;

    const productId = getProductIdForPlan(plan, billingPeriod);
    const product = CdvPurchase.store.get(productId, CdvPurchase.Platform.APPLE_APPSTORE);

    return product?.canPurchase || false;
  };

  // Add this function to the composable
  /**
   * Check if a plan/billing combination is available for purchase on the current platform
   * On native iOS, this checks if the IAP product exists and can be purchased
   * On web/non-native, always returns true (Stripe handles everything)
   */
  const isPlanAvailableForPurchase = (plan: SubscriptionPlan, billingPeriod: 'monthly' | 'yearly'): boolean => {
    // On non-native platforms, all plans are available (handled by Stripe)
    if (!isNative.value) {
      return true;
    }

    // On native iOS, check if the IAP product is available
    if (isIOS.value) {
      // Freemium is always available
      if (plan.name.toLowerCase() === 'freemium') {
        return true;
      }

      const productId = getProductIdForPlan(plan, billingPeriod);
      const product = availableProducts.value.find((p) => p.id === productId);

      // Product must exist and be purchasable
      return product !== undefined && product.canPurchase;
    }

    // Default: available
    return true;
  };

  /**
   * Present Apple's native offer code redemption sheet.
   * This opens iOS's built-in UI where users can enter an Apple Subscription Offer Code.
   * Apple validates the code and handles the subscription creation/modification.
   * The normal purchase flow (approved → receiptUpdated → validateReceiptWithBackend)
   * handles the rest automatically.
   * Requires iOS 14+ and cordova-plugin-purchase v13+.
   */
  /**
   * Request a refund for an Apple IAP transaction.
   * Uses a custom Capacitor plugin wrapping StoreKit 2's Transaction.beginRefundRequest (iOS 15+).
   * Falls back to Apple's "Report a Problem" page on older iOS versions.
   */
  const requestRefund = async (transactionId: string): Promise<'success' | 'cancelled' | 'unsupported' | 'error'> => {
    if (!isNative.value || !isIOS.value) return 'unsupported';

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { RefundPlugin } = (window as any).Capacitor?.Plugins ?? {};
      if (!RefundPlugin) {
        // Plugin not available — open Report a Problem web page as fallback
        window.open('https://reportaproblem.apple.com/', '_blank');
        return 'unsupported';
      }

      const result = await RefundPlugin.beginRefundRequest({ transactionId });
      return result?.status ?? 'error';
    } catch {
      // iOS < 15 or plugin error — fallback
      window.open('https://reportaproblem.apple.com/', '_blank');
      return 'unsupported';
    }
  };

  /**
   * Open the native manage-subscriptions sheet.
   * Uses cordova-plugin-purchase's manageSubscriptions() on native iOS,
   * falls back to the App Store web link otherwise.
   */
  const openManageSubscriptions = async (): Promise<void> => {
    if (isNative.value && isIOS.value && isInitialized.value) {
      try {
        await CdvPurchase.store.manageSubscriptions(CdvPurchase.Platform.APPLE_APPSTORE);
        return;
      } catch {
        // Fall through to web link
      }
    }
    window.open('https://apps.apple.com/account/subscriptions', '_blank');
  };

  const redeemOfferCode = async (): Promise<boolean> => {
    if (!isNative.value || !isIOS.value) return false;

    if (!isInitialized.value) {
      await initializeStore();
    }

    if (isProcessing.value) return false;

    try {
      isProcessing.value = true;
      pollingValidationDone = false;

      // Pre-register intent so the backend can associate the upcoming
      // OFFER_REDEEMED webhook with the correct restaurant (since
      // presentCodeRedemptionSheet does not support appAccountToken).
      try {
        await fetchData(`${envStore.apiUrl}/subscriptions/apple/offer-code-intent`, 'POST', {});
      } catch {
        // Best-effort: don't block redemption if intent registration fails.
      }

      const storeAny = CdvPurchase.store as unknown as Record<string, unknown>;
      const getAdapter = storeAny.getAdapter as ((platform: unknown) => Record<string, unknown>) | undefined;
      // Call with .call() to preserve the store's `this` context
      const appStore = getAdapter ? getAdapter.call(CdvPurchase.store, CdvPurchase.Platform.APPLE_APPSTORE) : null;

      if (!appStore || typeof appStore.presentCodeRedemptionSheet !== 'function') {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('subscription.iap.offerCode.notSupported'),
          life: 5000,
        });
        isProcessing.value = false;
        return false;
      }

      await appStore.presentCodeRedemptionSheet();

      startPurchaseCompletionPolling('offercoderedemption');
      return true;
    } catch (error) {
      isProcessing.value = false;
      const msg = error instanceof Error ? error.message : String(error);
      if (!msg.includes('cancel')) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('subscription.iap.offerCode.redeemFailed'),
          life: 5000,
        });
      }
      return false;
    }
  };
  return {
    // State
    isInitialized,
    isInitializing, // Expose this
    isProcessing,
    availableProducts,

    // Methods
    initializeStore,
    purchaseSubscription,
    restorePurchases,
    redeemOfferCode,
    requestRefund,
    openManageSubscriptions,
    getProductPrice,
    getApplePrice,
    getAppleMonthlyEquivalent,
    isProductAvailable,
    getProductIdForPlan,
    isPlanAvailableForPurchase,
  };
}
